<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <b-alert v-model="showTempError" dismissible fade variant="danger">
            {{ tempErrorText }}
        </b-alert>

        <base-single-collapse button-text="Instructions" expanded>
            <ul class="list-flush">
                <li>
                    Add <b><em>at least one</em></b> response item that
                    suppliers must complete -- a Question List, Quote Sheet, or
                    Document Upload, or Document Template.
                </li>
                <li>
                    Add additional response forms as needed. You can add as many
                    as you need, of any of the different types.
                </li>
            </ul>
        </base-single-collapse>

        <div class="container-fluid">
            <div class="row mt-4 mb-3">
                <div class="col">
                    <hr style="border-color: var(--success-200)" />
                </div>
                <div
                    class="col-auto d-flex justify-content-center align-items-center text-success text-uppercase font-weight-500 font-size-90"
                >
                    <div class="">Add Items for Vendors to Complete</div>
                </div>
                <div class="col">
                    <hr style="border-color: var(--success-200)" />
                </div>
            </div>
        </div>

        <div class="d-flex gap-4">
            <div
                v-for="toDoType in companyToDoTypes"
                :key="toDoType.key"
                class="border rounded add-todo-box d-flex"
                @click="showCreateModal(toDoType)"
            >
                <div
                    class="action-area py-2 px-1 width-70 d-flex flex-column justify-content-between align-items-center"
                >
                    <b-icon :icon="toDoType.icon" font-scale="1.5"></b-icon>
                    <div class="add-text text-center">
                        <b-icon icon="plus-circle" font-scale="1.5"></b-icon>
                        <div
                            class="text-center font-weight-500 font-size-70 mt-1"
                        >
                            ADD
                        </div>
                    </div>
                </div>
                <div class="w-100 px-3 py-2">
                    <h5>{{ toDoType.title }}</h5>
                    <div class="text-muted">{{ toDoType.description }}</div>
                </div>
            </div>
        </div>

        <b-alert
            variant="warning"
            :show="rfxHasSubmissions"
            class="mt-3 small"
            dismissible
            fade
        >
            <strong>Warning - Existing Submissions:</strong>
            Some suppliers have already submitted their responses. If you change
            any response items and require suppliers to update their responses,
            you should notify them using a direct message to each supplier, or
            using a bulk message from the
            <a :href="eventDashboardUrl" class="alert-link font-weight-normal"
                >event dashboard</a
            >.
        </b-alert>
        <b-alert
            variant="warning"
            :show="rfxCanVendorView"
            class="mt-3 small"
            dismissible
            fade
        >
            <strong>Warning - Event is visible to suppliers:</strong>
            The list of response items is currently visible to invited
            suppliers. We recommend setting the event status to 'DRAFT' while
            you are making changes to avoid confusing any suppliers who are
            currently working on their response.
            <a :href="eventDashboardUrl" class="alert-link font-weight-normal"
                >Go to Event Dashboard</a
            >
        </b-alert>

        <!-- <hr class="my-4"> -->

        <transition name="fade-fast" mode="out-in">
            <!-- LOADING -->
            <div
                v-if="loading"
                key="loadingList"
                class="d-flex justify-content-center align-items-center py-4 my-4"
            >
                <busy-spinner></busy-spinner>
            </div>

            <!-- Emtpy list, not loading -->
            <div
                v-else-if="rfxCompanyToDos.length === 0 && !loading"
                key="emptyRfxCompanyToDoList"
                class="d-flex justify-content-center align-items-center py-4 my-4"
            >
                <div class="text-center">
                    <b-icon icon="arrow-up" class="h2 mb-3 text-muted"></b-icon>
                    <div class="font-weight-bold text-muted font-size-100">
                        Select a response type
                    </div>
                    <div class="text-center text-muted small mt-3">
                        Must pick at least one. Add as many as needed.
                    </div>
                </div>
            </div>

            <div
                v-else-if="rfxCompanyToDos.length && !loading"
                key="rfxCompanyToDoList"
            >
                <div class="container-fluid">
                    <div class="row mt-4 mb-3">
                        <div class="col">
                            <hr style="" />
                        </div>
                        <div
                            class="col-auto d-flex justify-content-center align-items-center text-muted text-uppercase font-weight-500 font-size-90"
                        >
                            Response Items
                        </div>
                        <div class="col">
                            <hr style="" />
                        </div>
                    </div>
                </div>

                <draggable
                    :list="rfxCompanyToDos"
                    handle=".drag-handle"
                    animation="200"
                    :disabled="allDisabled"
                    @start="startDrag"
                    @end="endDrag"
                >
                    <transition-group
                        :name="!dragging ? 'rfxcompanytodo-list' : null"
                    >
                        <!-- apply transition only when NOT dragging -->
                        <div
                            v-for="todo in rfxCompanyToDos"
                            :key="todo.id"
                            class="rctd-todo d-flex justify-content-start align-items-stretch p-0 w-100 mb-3 border rounded shadow-sm overflow-hidden"
                            :class="[
                                refreshToDoId === todo.id ? 'opacity-50' : '',
                                deleteRfxCompanyToDoId === todo.id
                                    ? 'border-danger opacity-50'
                                    : '',
                                todo.status === 'HIDDEN' ? 'hidden-todo' : '',
                                todo.status === 'SUPPLIERLOCKED' ? 'supplierlocked-todo' : '',
                            ]"
                        >
                            <div
                                class="drag-handle d-flex justify-content-between align-items-center px-2 py-3 bg-primary-50 min-width-120 max-width-120"
                                style="border-left: 4px solid var(--primary)"
                            >
                                <div>
                                    <b-icon
                                        icon="grip-vertical"
                                        font-scale="2"
                                        class="text-gray-400"
                                    ></b-icon>
                                </div>
                                <div
                                    class="flex-grow-1 text-center text-primary-600"
                                >
                                    <b-icon
                                        :icon="todo.categoryIcon"
                                        font-scale="1.7"
                                    ></b-icon>
                                    <div class="small mt-1">
                                        {{ todo.categoryName }}
                                    </div>
                                </div>
                            </div>
                            <div
                                class="container py-2"
                            >  <!-- d-flex flex-wrap justify-content-between align-items-center -->
                                <div class="h-100 row">
                                    <div class="col-12 col-xl-4 py-2 d-flex align-items-center justify-content-center">  <!-- w-30 px-3 -->
                                        <div class="w-100">
                                            <h5 class="mb-0 text-center text-lg-left">{{ todo.title }}</h5>
                                            <p
                                                v-if="todo.instructions"
                                                class="mb-0 mt-2 text-muted text-center text-lg-left small"
                                            >
                                                {{ todo.instructions }}
                                            </p>
                                        </div>
                                    </div>

                                    <div
                                        class="col-12 col-xl-8 py-2 py-xl-0"
                                    >  <!-- w-70 -->
                                        <div class="h-100 row">
                                            <!-- TOGGLE SWITCHEs -->
                                            <div class="col-12 col-xl-6 py-2 py-xl-0 d-flex flex-row align-items-center justify-content-center" style="row-gap: 0.5rem; column-gap: 3rem;">
                                                <div class="min-width-80">
                                                    <div
                                                        class="text-uppercase font-weight-500 font-size-80"
                                                        v-b-tooltip="{
                                                            delay: { show: 500, hide: 50 },
                                                            title: 'If required, suppliers will be blocked from submitting their response until this item is complete.'
                                                        }"
                                                    >
                                                        Required?
                                                    </div>
                                                    <b-form-checkbox
                                                        switch
                                                        inline
                                                        :disabled="allDisabled || listBusy"
                                                        v-model="todo.required"
                                                    >
                                                        <span
                                                            class="text-uppercase font-weight-500 font-size-80 text-muted"
                                                            :class="
                                                                todo.required
                                                                    ? ''
                                                                    : 'text-muted'
                                                            "
                                                        >
                                                            {{
                                                                todo.required ? "Yes" : "No"
                                                            }}
                                                        </span>
                                                    </b-form-checkbox>
                                                </div>
                                                <div class="min-width-80">
                                                    <div
                                                        class="text-uppercase font-weight-500 font-size-80"
                                                        v-b-tooltip.hover="{
                                                            delay: { show: 500, hide: 50 },
                                                            title: 'Hidden items are not shown to suppliers, but still visible internally along with any existing responses.'
                                                        }"
                                                    >
                                                        Hidden?
                                                    </div>  <!-- TODO - make this a dropdown to also support SUPPLIERLOCKED status when you're ready to start using that -->
                                                    <b-form-checkbox
                                                        switch
                                                        inline
                                                        :disabled="allDisabled || listBusy"
                                                        value="HIDDEN"
                                                        unchecked-value="ENABLED"
                                                        v-model="todo.status"
                                                    >
                                                        <span
                                                            class="text-uppercase font-weight-500 font-size-80 text-muted"
                                                            :class="
                                                                todo.status === 'HIDDEN'
                                                                    ? ''
                                                                    : 'text-muted'
                                                            "
                                                        >
                                                            {{
                                                                todo.status === 'HIDDEN' ? "Yes" : "No"
                                                            }}
                                                        </span>
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        
                                            <div class="col-12 col-xl-6 py-2 py-xl-0 d-flex justify-content-center align-items-center">
                                                <!-- VALIDATION and STATUS display -->
                                                <div class="d-flex flex-row flex-xl-column gap-2 align-items-center justify-content-center">
                                                    <div
                                                        v-if="todo.status === 'HIDDEN'"    
                                                        class=""
                                                    >
                                                        <div
                                                            :id="`todo-hidden-${todo.id}`"
                                                            class="d-flex align-items-center text-warning-darker px-1 bg-warning-100 border border-warning rounded-pill"
                                                        >
                                                            <b-icon
                                                                icon="eye-slash-fill"
                                                                class="mr-1"
                                                            ></b-icon>
                                                            <span
                                                                class="text-uppercase font-size-80 font-weight-bold"
                                                                >Hidden</span
                                                            >
                                                        </div>
                                                        <b-popover
                                                            :target="`todo-hidden-${todo.id}`"
                                                            title="Hidden Item"
                                                            triggers="hover focus"
                                                            variant="warning"
                                                            placement="top"
                                                        >
                                                            <p class="mb-0">
                                                                This item is hidden from suppliers, but any existing responses will still be visible internally.
                                                            </p>
                                                        </b-popover>
                                                    </div>
                                                    <div
                                                        v-if="todo.status === 'SUPPLIERLOCKED'"    
                                                        class=""
                                                    >
                                                        <div
                                                            :id="`todo-supplierlocked-${todo.id}`"
                                                            class="d-flex align-items-center text-secondary-darker px-1 bg-secondary-100 border border-secondary rounded-pill"
                                                        >
                                                            <b-icon
                                                                icon="lock-fill"
                                                                class="mr-1"
                                                            ></b-icon>
                                                            <!-- NOTE: this is SUPPLIER LOCKED - different than internal locked, which is also a concept here -->
                                                            <span
                                                                class="text-uppercase font-size-80 font-weight-bold"
                                                                >Locked</span
                                                            >
                                                        </div>
                                                        <b-popover
                                                            :target="`todo-supplierlocked-${todo.id}`"
                                                            title="Locked For Suppliers"
                                                            triggers="hover focus"
                                                            variant="secondary"
                                                            placement="top"
                                                        >
                                                            <p class="mb-0">
                                                                This item is still visible to suppliers, but they cannot edit their responses.
                                                            </p>
                                                        </b-popover>
                                                    </div>
                                                    <div
                                                        v-if="!todo.isValid"
                                                        class=""
                                                    >  <!-- VALIDITY STUFF -->
                                                        <div class="d-flex flex-column align-items-center gap-1">
                                                            <div
                                                                :id="`todo-errors-${todo.id}`"
                                                                class="d-flex align-items-center text-danger px-1 bg-danger-50 border border-danger rounded-pill"
                                                            >
                                                                <b-icon
                                                                    icon="exclamation-circle-fill"
                                                                    class="mr-1"
                                                                ></b-icon>
                                                                <span
                                                                    class="text-uppercase font-size-80 font-weight-bold"
                                                                    >Invalid</span
                                                                >
                                                            </div>
                                                            <div
                                                                class="d-none d-xl-block text-danger font-size-70 font-weight-light"
                                                                >Hover for errors</div
                                                            >
                                                        </div>
                                                        <b-popover
                                                            :target="`todo-errors-${todo.id}`"
                                                            title="Errors"
                                                            triggers="hover focus"
                                                            variant="danger"
                                                            placement="top"
                                                        >
                                                            <p
                                                                v-for="errMsg in todo.validationErrors"
                                                                :key="errMsg"
                                                                class="mb-1 font-italic"
                                                            >
                                                                {{ errMsg }}
                                                            </p>
                                                            <hr class="my-2" />
                                                            <p class="mb-0">
                                                                This item will be hidden
                                                                from suppliers until it is
                                                                fixed.
                                                            </p>
                                                        </b-popover>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                class="px-2 d-flex justify-content-end align-items-center"
                            >
                                <!-- Right -->
                                <div class="d-flex flex-column align-items-start justify-content-center gap-2">
                                    <!-- ACTION BUTTONS -->
                                    <b-button
                                        v-if="todo.companyToDo.canClone"
                                        variant="minimal-primary"
                                        size="sm"
                                        class="mr-2 text-nowrap"
                                        :disabled="allDisabled || listBusy"
                                        @click="startClone(todo)"
                                    >   <!-- only show this for smartforms, or other things that can be cloned -->
                                        <b-icon icon="files" class="mr-1"></b-icon>
                                        Clone
                                    </b-button>

                                    <b-button
                                        variant="minimal-danger"
                                        size="sm"
                                        class="mr-2 text-nowrap"
                                        :disabled="allDisabled || listBusy"
                                        @click="startDelete(todo)"
                                    >
                                        <b-icon icon="trash" class="mr-1"></b-icon>
                                        Delete
                                    </b-button>
                                </div>

                                <b-button
                                    variant="minimal-primary"
                                    class="d-flex align-items-center px-2 py-2 text-nowrap"
                                    size="lg"
                                    :disabled="listBusy"
                                    @click="editToDo(todo)"
                                >  <!-- we are never disabling this, instead, we set all the things it shows to be read-only if this.isReadOnly -->
                                    <!-- <b-icon icon="pencil-square" class="mr-2"></b-icon> -->
                                    View / Edit
                                    <b-icon
                                        icon="chevron-right"
                                        font-scale="1.5"
                                        class="ml-2"
                                    ></b-icon>
                                </b-button>
                            </div>
                        </div>
                    </transition-group>
                </draggable>
            </div>
        </transition>

        <!-- <div>
            <b-link class="small mx-2" @click="dumpApiData">Dump API Data</b-link>
            <b-link class="small mx-2" @click="updateToDoList">Update MetaData</b-link>
        </div> -->

        <!-- <div>
            <h4>RfxCompanyToDos</h4>
            <pre>{{ rfxCompanyToDos }}</pre>
        </div> -->

        <b-modal
            v-model="deleteModalShowing"
            centered
            size="sm"
            hide-header
            hide-footer
            @hidden="onDeleteModalHidden"
        >
            <h4 class="mb-3">Delete {{ deleteObject.title }}?</h4>
            <p>Are you sure you want to delete this item?</p>
            <div
                v-if="deleteObject.hasReplies"
                class="small alert alert-danger"
            >
                <p>
                    Some suppliers have already
                    {{
                        deleteObject.hasSubmittedReplies
                            ? "submitted"
                            : "started"
                    }}
                    responses to this item.
                </p>
                <p>
                    If you continue to delete this item, those responses will be
                    <strong>permanently deleted.</strong>
                </p>
                <p>
                    If you still want to continue deleting this item, please
                    type <em>delete</em> in the box below.
                </p>
                <b-form-input
                    v-model="deleteConfirmMessage"
                    :state="highlightDeleteConfirmMessage ? false : null"
                    placeholder="type 'delete' to confirm"
                ></b-form-input>
            </div>
            <div class="mt-3 text-center">
                <!-- custom buttons to better control behavior -->
                <b-button
                    variant="secondary"
                    class="mr-3"
                    @click="deleteModalShowing = false"
                    >Cancel</b-button
                >
                <b-button variant="danger" class="" @click="performDelete"
                    >Yes, Delete</b-button
                >
            </div>
        </b-modal>

        <b-modal
            v-model="createModalShowing"
            centered
            size="md"
            hide-header
            hide-footer
            @show="onCreateModalShow"
            @hidden="onCreateModalHidden"
        >
            <busy-spinner
                v-if="showCreateSpinner"
                key="creating"
                inner-class="my-4"
            ></busy-spinner>
            <div v-else key="notCreating">
                <h4>Add new {{ createObject.category_title }}</h4>
                
                <b-form-group
                    v-if="createObject.key == 'smartform'"
                    id="new-smartform-template-fieldset"
                    label="Choose a template:"
                    label-for="new-smartform-template-select"
                    label-class="font-weight-bold"
                    :description="`Choose which template to use.`"
                    class="mt-3"
                >
                    <b-form-select
                        id="new-smartform-template-select"
                        :value="createObject.smartform_template_id"
                        @input="handleSmartFormTemplateSelected"
                        :options="smartFormTemplateOptions"
                        value-field="id"
                        text-field="name"
                        :disabled="loadingSmartFormTemplateOptions"
                        :autofocus="createObject.key == 'smartform'"
                        size="lg"
                    >
                        <template #first>
                            <b-form-select-option :value="''" disabled>
                                {{ smartFormTemplateOptions.length ? '-- Select a template --' : 'No templates available' }}
                            </b-form-select-option>
                        </template>
                    </b-form-select>
                </b-form-group>

                <b-form-group
                    v-if="createObject.key == 'quotesheet'"
                    id="new-quotesheet-template-fieldset"
                    label="Choose a template:"
                    label-for="new-quotesheet-template-select"
                    label-class="font-weight-bold"
                    :description="`Choose which template to use to create the Quote Sheet (optional).`"
                    class="mt-3"
                >
                    <!-- still showing this if no templates?  no, works ok, but just confusing -->
                    <b-form-select
                        id="new-quotesheet-template-select"
                        :value="createObject.quotesheet_template_id"
                        @input="handleQuoteSheetTemplateSelected"
                        :options="quoteSheetTemplateOptions"
                        value-field="id"
                        text-field="title"
                        :disabled="loadingQuoteSheetTemplateOptions"
                        :autofocus="createObject.key == 'quotesheet'"
                        size="lg"
                    >
                        <template #first>
                            <b-form-select-option :value="''">  <!-- do not disable this option - empty string is valid -->
                                {{ quoteSheetTemplateOptions.length ? '-- Select optional template --' : 'No templates available' }}
                            </b-form-select-option>
                        </template>
                    </b-form-select>
                </b-form-group>
                
                
                <b-form-group
                    id="new-todo-title-fieldset"
                    label="Title"
                    label-for="new-todo-title"
                    label-class="font-weight-bold"
                    :description="`Provide a title for this ${createObject.category_title}.  Will be visible to vendors.  You can change it later.`"
                    class="mt-3"
                >
                    <b-form-input
                        id="new-todo-title"
                        v-model="createObject.title"
                        trim
                        size="lg"
                        :autofocus="createObject.key != 'smartform'"
                        @keyup.enter="onCreateModalOk"
                    ></b-form-input>
                </b-form-group>

                <div
                    class="mt-3 d-flex justify-content-center align-items-center"
                >
                    <b-button
                        variant="gray-secondary"
                        class="mr-4"
                        size="lg"
                        @click="createModalShowing = false"
                        >Cancel</b-button
                    >
                    <b-button
                        :disabled="!createObjectValid"
                        variant="primary"
                        size="lg"
                        @click="onCreateModalOk"
                        >Add</b-button
                    >
                </div>
            </div>
        </b-modal>

        <div>
            <!-- ToDo Edit Modals -->

            <b-modal
                v-model="quoteSheetModalShowing"
                centered
                size="full"
                title="Quote Sheet Builder"
                body-class="d-flex flex-column bg-gray-300 p-0"
                hide-footer
                hide-header-close
                no-close-on-esc
                no-close-on-backdrop
                @hide="finishEditToDo"
            >
                <!-- Just wants QuoteSheet ID -->
                <quote-sheet-builder
                    :quote-sheet-id="activeQuoteSheetId"
                    :read-only="isReadOnly"
                    @request-close="quoteSheetModalShowing = false"
                ></quote-sheet-builder>
            </b-modal>

            <b-modal
                v-model="formiumFormModalShowing"
                centered
                size="xxl"
                title="Form Builder"
                body-class="bg-gray-100"
                content-class="overflow-hidden"
                no-close-on-esc
                no-close-on-backdrop
                hide-footer
                hide-header-close
                @hide="finishEditToDo"
            >
                <formium-form-builder
                    :form-id="activeFormiumFormId"
                    :read-only="isReadOnly"
                    @close-me="formiumFormModalShowing = false"
                ></formium-form-builder>
                <!-- just wants form ID -->
            </b-modal>

            <b-modal
                v-model="docRequestModalShowing"
                centered
                size="md"
                title="File Upload"
                body-class="bg-gray-100"
                content-class="overflow-hidden"
                no-close-on-esc
                no-close-on-backdrop
                hide-footer
                hide-header
                @hide="finishEditToDo"
            >
                <doc-request-setup
                    :doc-request-id="activeDocRequestId"
                    :read-only="isReadOnly"
                    @save-close="docRequestModalShowing = false"
                    @cancel-close="docRequestModalShowing = false"
                ></doc-request-setup>
                <!-- DocRequest setup wants the whole object as prop -->
            </b-modal>

            <b-modal
                v-model="smartFormModalShowing"
                :key="activeSmartFormId"
                centered
                size="xxl"
                title="Quote Worksheet"
                body-class="bg-gray-100"
                content-class="overflow-hidden"
                no-close-on-esc
                no-close-on-backdrop
                hide-footer
                hide-header
                scrollable
                @hide="finishEditToDo"
            >  <!-- added :key to make sure modal is destroyed if activeSmartFormId is ever changed when modal is alive -->
                <SmartFormFormio
                    :form-id="activeSmartFormId"
                    :read-only="isReadOnly"
                    @request-close="smartFormModalShowing = false"
                />  <!-- it also fires 'hide' and 'update' events - but those are mainly used for nested smartforms in other smartforms -->
                <!-- DocRequest setup wants the whole object as prop -->
            </b-modal>

            <!-- <SmartForm 
                :form-id="activeSmartFormId"
                :isShown="isSmartFormModalShown"
                @hide="finishEditToDo"
                @update:isShown="
                    (newValue) => (isSmartFormModalShown = newValue)
                "
            /> -->
        </div>
    </div>
</template>

<script>
//import { HotEditSwitch } from '../js/custom-form-components';
import { errorHandling, tenantInfoMixin } from "../../js/custom-vue-mixins";
import { suppliosApi } from "../../js/api-service";
import draggable from "vuedraggable";
import debounce from "lodash/debounce";
import {
    companyToDoTypes,
    companyToDoTypesMapping,
} from "../company_todos/company_todo_types";
import { DateTime } from "luxon";
import cloneDeep from "lodash/cloneDeep";
import SmartFormFormio from '../smartforms/SmartFormFormio.vue';

class CompanyToDo {
    // represents a CompanyToDo, possibly with internal-only data, or with reply data, depending on where its used
    // this gets nested inside other use-case-specific ToDo classes like RfxCompanyToDo
    // those other classes may pull data out of here, etc. --> those are the ones used in Vue components UI
    constructor({
        // set defaults here - make sure to use them because some things are not populated for internal/external use cases
        id = "",
        category = "",
        subcategory = "", // may be empty string, only used for some categories - if present, gets appended to category to make 'category_key'
        title = "",
        instructions = "", // instructions on the CompanyToDo itself -- may get superceded by some class that 'wraps' this one
        linked_model_id = "",
        linked_model = {},
        created = "", // will be ISO 8601 string
        modified = "", // will be ISO 8601 string
        reply_count = null, // you may or may not have this from API, depending on qs annotation
        is_valid, // boolean
        validation_errors, // a list/array of strings
    }) {
        // set all things from args object here with this.arg = arg;
        this.id = id;
        this.category = category; // something like 'docrequest', 'formium_form', 'quotesheet'
        this.subcategory = subcategory;
        this.category_key = subcategory
            ? category + "_" + subcategory
            : category; // used for lookups in list/mapping above
        this.title = title;
        this.instructions = instructions; // should probably get this from 'final_instructions' on use-case-specific model
        this.linked_model_id = linked_model_id;
        this.linked_model = linked_model;
        this.created = created ? DateTime.fromISO(created) : null;
        this.modified = modified ? DateTime.fromISO(modified) : null;
        this.reply_count = reply_count || null; // may not have it -- might just be on 'parent' class (RfxCompanyToDo)
        this.is_valid = is_valid;
        this.validation_errors = validation_errors;
    }
    // GETTERS
    get toDoType() {
        // use this in other getters, DRY
        return companyToDoTypesMapping[this.category_key];
    }
    get categoryName() {
        return this.toDoType.title;
    }
    get categoryIcon() {
        return this.toDoType.icon;
    }
    get canClone() {
        return this.toDoType.canClone;
    }
    // METHODS
}

class RfxCompanyToDo {
    // all the RfxCompanyToDo fields, with CompanyToDo nested inside
    constructor({
        id = "", // ID of the RfxCompanyToDo
        final_instructions = "", // from @property -- uses instructions from RfxCompanyToDo, with fallback to CompanyToDo - let the backend do the work for consistency
        instructions = "", // these are instructions stored on RfxCompanyToDo instance, not nested CompanyToDo
        position = null, // integer sort order
        required = true, // Boolean
        status = 'ENABLED',  // 'ENABLED', 'HIDDEN', 'SUPPLIERLOCKED' are choices for now
        company_todo, // NESTED CompanyToDo - don't need a default... it will be an object, we'll create new CompanyToDo instance inside constructor
        rfx = "", // ID of Rfx
        created = "", // ISO 8601 string
        modified = "", // ISO 8601 string
        reply_count = null, // will be integer if you have it - from queryset annotation, fallback to model method
        submitted_reply_count = null, // will be integer if you have it - from queryset annotation, fallback to model method
    }) {
        this.id = id; // ID of the RfxCompanyToDo
        this.final_instructions = final_instructions;
        this.instructions = instructions;
        this.position = position;
        this.required = required;
        this.status = status;
        // OLD this.companyToDo = new CompanyToDoInternal(company_todo);
        this.companyToDo = new CompanyToDo(company_todo);
        this.rfx = rfx;
        this.created = created;
        this.modified = modified;
        this.reply_count = reply_count;
        this.submitted_reply_count = submitted_reply_count;
    }
    // GETTERS
    get toDoType() {
        // use this in other getters, DRY
        return this.companyToDo.toDoType; // get from nested CompanyToDo instance for consistency
    }
    get categoryName() {
        return this.toDoType.title;
    }
    get categoryIcon() {
        return this.toDoType.icon;
    }
    get hasReplies() {
        return !!this.reply_count; // maybe same as isLocked, but maybe you want to change them...
    }
    get hasSubmittedReplies() {
        return !!this.submitted_reply_count;
    }
    get isLocked() {
        // this is INTERNAL LOCKED - different from supplier locked!
        return !!this.reply_count; // do we have more than 0 replies?  if so, consider it locked, at least show warning message
    }
    // pull some things out of company_todo / CompanyToDo up to top level, just for easiser use and backwards compatibility
    get category() {
        return this.companyToDo.category || ""; // get from nested CompanyToDo instance
    }
    get company_todo_id() {
        return this.companyToDo.id || ""; // get from nested CompanyToDo instance
    }
    get title() {
        return this.companyToDo.title || ""; // get from nested CompanyToDo instance
    }
    get linked_model_id() {
        return this.companyToDo.linked_model_id || ""; // get from nested CompanyToDo instance
    }
    get isValid() {
        return this.companyToDo.is_valid; // Boolean - get from nested CompanyToDo
    }
    get validationErrors() {
        return this.companyToDo.validation_errors; // LIST/Array - get from nested CompanyToDo
    }
    // METHODS
    formatForApi(index) {
        // pass in the index of this todo in the overall array to set new position
        // watch out for final_instructions / instructions... there is instructions field on RfxCompanyToDo and CompanyToDo.linked_model...
        let apiObj = { ...this }; // for now just make a copy
        apiObj.position = index + 1;
        return apiObj;
    }
}

export default {
    name: "RfxSetupResponse",
    components: {
        draggable,
        SmartFormFormio,
    },
    mixins: [errorHandling, tenantInfoMixin],
    props: {
        rfxId: {
            type: [String, Number],
            required: true,
        },
        isReadOnly: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            errorMsg: "Error",
            error: false,
            loading: false, // flag during initial loading of list
            creating: false, // flag for when doing create new rfxCompanyToDo
            updating: false, // flag for when we're updating the list metadata
            tempErrorText: "",
            showTempError: false,
            // RFX related stuff
            eventDashboardUrl: "", // will be the relative URL (path) to the event dashboard
            rfxHasSubmissions: false, // we get this from API call -- are there any RfxResponseSubmission objects?
            rfxCanVendorView: false, // from API - rfx.can_vendor_view -- if true, suggest that we move to draft
            sm_state: "", // the state of Rfx
            state_dict: {}, // full state dict of the Rfx state
            showPriceSheetModal: false,
            showFormBuilderModal: false,
            activePriceSheetPk: null, // set this when calling modal for price sheets
            activeFormBuilderPk: null, // set this when calling modal for question forms
            // stuff for old things above, new component versions below:
            newPriceSheetData: {
                name: "Price Sheet",
            },
            newQuestionListData: {
                title: "Question List",
            },
            newRfxCompanyToDoDocRequest: {
                // must be in the nested format expected by RfxCompanyToDoWriteSerializer
                category: "docrequest",
                docrequest: {
                    title: "Document Request",
                },
            },
            newRfxCompanyToDoFormiumForm: {
                // must be in the nested format expected by RfxCompanyToDoWriteSerializer
                category: "formium_form",
                formium_form: {
                    title: "Super Form",
                },
            },
            allowExtraUploads: true, // need methods for getting / saving this!
            // below are item counts, child components emit as 'item-count' event
            // seems these are not being used as of May 6 2022
            formCount: 0,
            pricingCount: 0,
            documentCount: 0,
            formiumCount: 0,
            // new stuff Jan 22 2021
            rfxCompanyToDos: [], // list of RfxCompanyToDo class instances
            // MOVED TO COMPUTED TO FILTER ENABLED TYPES companyToDoTypes: companyToDoTypes, // array of objects for different types of ToDos to be added -- easier to define here than in template
            // current linked_model ID being edited / updated -- NOT the RfxCompanyToDo, but the ID for linked_model inside RfxCompanyToDo
            activeQuoteSheetId: "",
            activeFormiumFormId: "",
            activeDocRequestId: "",
            activeSmartFormId: "",
            // v-models for various edit/update modals
            quoteSheetModalShowing: false,
            formiumFormModalShowing: false,
            docRequestModalShowing: false,
            smartFormModalShowing: false,
            // updating / refreshing / deleting
            deleteRfxCompanyToDoId: "", // set this ID when confirming to delete RfxCompanyToDo to show visually
            activeRfxCompanyToDoId: "", // ID of the RfxCompanyToDo - track that too, so you can refresh it easily
            refreshToDoId: "", // when you're refreshing an rfxCompanyToDo from API (after editing it) set the ID here, which will make it look different in the list
            dragging: false, // set to true when dragging draggable items, used to control transition-group animations
            // create modal - to capture title of whatever being added
            createModalShowing: false,
            defaultCreateObject: {
                // never mutate this... just change createObject
                title: "",
                key: "", // the 'key' for this type of todo from our list (similar to category, but slight differences)
                category: "",
                category_title: "",
                smartform_template_id: "",  // only used for smartforms
                quotesheet_template_id: "",  // only used for quotesheets - and OPTIONAL
                dr_type: "",  // only used for docrequests
            },
            createObject: {}, // populate this when user is adding things... clone from defaultCreateObject, then set things, then reset by cloning again
            showCreateSpinner: false, // show spinner in createModal?
            // delete modal - more than just a confirm message modal - to confirm deletes if there are replies, etc.
            deleteModalShowing: false, // v-model to show/hide modal
            deleteObject: {}, // do Object.assign({}, rctdObj) when setting this to make it reactive
            deleteConfirmMessage: "", // v-model to input, for typing 'delete' to confirm deletes when there are already replies
            highlightDeleteConfirmMessage: false, // if true, shows 'highlighted/error' state on deleteConfirmMessage input,
            // SMART FORMS - (QuoteWorksheet)
            // OLD isSmartFormModalShown: false,
            loadingSmartFormTemplateOptions: false,
            smartFormTemplateOptions: [],  // get from API on mounted()
            // QuoteSheet TEMPLATES - Jan 24 2023
            loadingQuoteSheetTemplateOptions: false,
            quoteSheetTemplateOptions: [], // get from API on mounted (same as other templates)
            // CLONING
            loadingClone: false, // set to true when cloning something
        };
    },
    computed: {
        totalItemCount: function () {
            return this.formCount + this.pricingCount + this.documentCount;
        },
        listBusy() {
            return this.loading || this.creating || this.updating || this.loadingClone || this.dragging || this.deleteModalShowing;
        },
        allDisabled() {
            // return boolean if everything is disabled from being in archived, etc
            // NOV 9 2022 - or if user is not event admin (we allow decision team to view in read only in some tenants)
            return this.isReadOnly || (this.sm_state === "archived");
        },
        showSmartForms() {
            // check the feature flag using tenantInfoMixin
            return this.tenantInfo?.custom_flags?.show_smartforms;
        },
        companyToDoTypes() {
            // only return types that are enabled
            return companyToDoTypes.filter(tdt => {
                if (tdt.key == 'smartform') {
                    return this.showSmartForms;
                } else {
                    return true;
                }
            });
        },
        createObjectValid() {
            // are all inputs for the current createObject valid?  use this for :disabled of the 'ADD' button (opposite of this)
            const alwaysRequiredThingsOk = !!this.createObject.title && !!this.createObject.key;
            // relevant things in createObject: title, category, smartform_template_id, quotesheet_template_id, dr_type, key  (key combines category + dr_type if you have docrequest... its redundant...)
            let categorySpecificOk;  // default...
            if (this.createObject.category == 'docrequest') {
                categorySpecificOk = !!this.createObject.dr_type;
            } else if (this.createObject.category == 'smartform') {
                categorySpecificOk = !!this.createObject.smartform_template_id;
            }  else {
                categorySpecificOk = true;
            }
            // don't need validation for quotesheet_template_id -- because its OPTIONAL - some tenants may have templates, some may not
            return alwaysRequiredThingsOk && categorySpecificOk;
        },
        hasQuoteSheetTemplates() {
            return !!this.quoteSheetTemplateOptions && Array.isArray(this.quoteSheetTemplateOptions) && this.quoteSheetTemplateOptions.length;
        },
    },
    watch: {
        rfxCompanyToDos: {
            deep: true,
            handler: function () {
                // THIS WORKS
                if (this.loading) return; // don't call update if we're still loading
                this.debouncedUpdateList();
            },
        },
    },
    filters: {
        dateToLocale: function (value) {
            return new Date(value).toLocaleDateString();
        },
    },
    created: function () {
        this.debouncedUpdateList = debounce(this.updateToDoList, 200);
        this.resetCreateObject();
        // call this from watcher
    },
    mounted: function () {
        this.getRfxCompanyToDos();
        // we reload these in onCreateModalShow, so its not stale 
        this.getSmartFormTemplateOptions();
        this.getQuoteSheetTemplateOptions();
    },
    methods: {
        startDrag: function () {
            this.dragging = true;
            console.log("START DRAG");
        },
        endDrag: function () {
            this.dragging = false;
            console.log("END DRAG");
        },
        // FETCHING OPTIONS FOR VARIOUS TEMPLATES
        // ----------------------------------------------------------------------------
        async getSmartFormTemplateOptions() {
            if (this.allDisabled) {
                return;  // just bail if things are read only 
            }
            // load the list of available SmartFormTemplates to add to this
            this.loadingSmartFormTemplateOptions = true;
            try {
                // set query_params to only get templates that can be top-level
                const axiosConfig = {
                    params: {
                        allow_top_level: true,
                    }
                };
                const { data } = await suppliosApi.smartFormTemplate.list(axiosConfig);
                this.smartFormTemplateOptions = data;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingSmartFormTemplateOptions = false;
        },
        handleSmartFormTemplateSelected(selectedTemplate) {
            console.log('selectedTemplate', selectedTemplate);
            this.createObject.smartform_template_id = selectedTemplate;
            const templateObj = this.smartFormTemplateOptions.find(t => (t.id == selectedTemplate));
            if (templateObj) {
                this.createObject.title = templateObj.default_form_name;
            }
        },
        async getQuoteSheetTemplateOptions() {
            if (this.allDisabled) {
                return;  // just bail if things are read only 
            }
            // load the list of available templates to add to this
            this.loadingQuoteSheetTemplateOptions = true;
            try {
                const { data } = await suppliosApi.quoteSheet.list_templates();
                this.quoteSheetTemplateOptions = data;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingQuoteSheetTemplateOptions = false;
        },
        handleQuoteSheetTemplateSelected(selectedTemplateId) {
            console.log('selectedTemplateId', selectedTemplateId);
            this.createObject.quotesheet_template_id = selectedTemplateId ?? '';  // could be empty? its optional...
            /* NOT OVERWRITING TITLE FROM TEMPLATE FOR NOW - IF YOU DID, THEN TRACK IF INPUT IS DIRTY TO NOT ERASE ANYTHING USER ENTERED ALREADY
            const templateObj = this.quoteSheetTemplateOptions.find(t => (t.id === selectedTemplateId));
            if (templateObj) {
                this.createObject.title = templateObj.title;  // maybe don't copy the title for these templates???
            } */
        },
        //////////////////////
        getRfxCompanyToDos: function () {
            this.loading = true;
            return suppliosApi.rfx
                .listRfxCompanyToDos(this.rfxId)
                .then((response) => {
                    let {
                        todos,
                        rfx_dashboard_url,
                        can_vendor_view,
                        has_submissions,
                        sm_state,
                        state_dict,
                    } = response.data; // you have multiple keys in response.data
                    this.eventDashboardUrl = rfx_dashboard_url;
                    this.rfxHasSubmissions = has_submissions;
                    this.rfxCanVendorView = can_vendor_view;
                    this.sm_state = sm_state;
                    this.state_dict = Object.assign({}, state_dict);
                    let rawList = todos; // response.data should be array/list of RfxCompanyToDos from backend serializer
                    let instanceList = []; // start with a new list, fill with instances of RfxCompanyToDo JS class
                    rawList.forEach((todo) => {
                        let newToDo = new RfxCompanyToDo(todo);
                        instanceList.push(newToDo); // put them in local data
                    });
                    this.rfxCompanyToDos = instanceList; // set some other way?  Vue.set?
                    return true;
                })
                .catch((error) => {
                    this.apiErrorHandler(error);
                    return false;
                })
                .then((success) => {
                    this.loading = false;
                    return success;
                });
        },
        showCreateModal: function (toDoType) {
            // show small modal to capture 'title' for whatever you're creating, then pass that to createToDo
            // this also serves to make 'add new' ToDo process less jarring, easier to understand
            if (this.allDisabled) return;  // don't do anything if we're in archived, etc.
            this.createObject.key = toDoType.key;
            this.createObject.title = toDoType.default_title; // the default title to start with in the input - can be edited
            this.createObject.category = toDoType.category;
            this.createObject.category_title = toDoType.title;
            this.createObject.smartform_template_id = '';  // if its smartform, we have extra select box with v-model to this
            this.createObject.quotesheet_template_id = '';  // for quotesheets -- OPTIONAL - if there are any template options for this tenant, we show extra v-model select for this
            this.createObject.dr_type = toDoType.options?.dr_type;  // only docrequests have these
            this.createModalShowing = true;
        },
        onCreateModalOk: function () {
            // called when user clicks OK on the createModal OK/Create button -- should have text entered for title
            // they enter text for title, that is v-model to this.createObject.title
            // show spinner, start the createToDo process, then close modal when create is done (use promise)
            if (!this.createObjectValid) {
                // ADD button is disabled if this is the case, but still putting here...
                this.$toast.warning(`Please fill out all information`);
                return;
            }
            this.showCreateSpinner = true; // spinner gets turned off on 'hidden' hook
            this.createToDo(this.createObject).then(() => {
                // createToDo returns boolean based on success or not... we will just do same thing whether true or false
                this.createModalShowing = false;
            }); // make sure this gets triggered before modal closes so reset doesn't wipe it out
        },
        onCreateModalHidden: function () {
            // reset some things
            this.resetCreateObject();
            this.showCreateSpinner = false;
        },
        onCreateModalShow() {
            // load / refresh the options for various templates
            this.getSmartFormTemplateOptions();
            this.getQuoteSheetTemplateOptions();
        },
        resetCreateObject: function () {
            // DRY way to set the empty / default createObject -- call on created/mounted, then anytime you want to reset
            this.createObject = Object.assign({}, this.defaultCreateObject); // clone this to set the defaults
        },
        getDefaultTitle: function (toDO) {
            toDO ? toDO.title : ''
        },
        createToDo(toDo) {
            if (this.allDisabled) {
                return;  // just bail if things are read only 
            }
            // one generic method to make any type of todo -- key will be 'formium_form', 'quotesheet', 'docrequest'
            // build data for create --> call API to create it, get new RfxCompanyToDo back --> call this.editToDo which sets active object to edit and opens correct modal
            // must put createData in correct structure for RfxCompanyToDoSerializer (company_todo is nested inside)

            this.creating = true;
            console.log("createToDo: toDo: ", toDo);

            // pull out the things you'll work with from the create object (toDo)
            const { title, category, smartform_template_id, quotesheet_template_id, dr_type } = toDo;
            const rfxId = this.rfxId;
            const position = this.rfxCompanyToDos.length + 1;

            // top-level data for RfxCompanyToDo object -- we'll put companyToDoData inside before we POST
            // data should include keys for 'rfx' (this.rfxId), 'category', '[category_name]: {data for create, but may be optional depending on category}', 'position' (optional, but easy... pass 1-based position of new last position in the list)
            const data = {
                // need rfx.id in data object if you use RfxCompanyToDo endpoint... but not if you use rfx @action endpoint
                rfx: rfxId,
                // set position to bottom of list for now
                position,
                // let the default in backend handle it -- or if/when you make a popup modal to capture title, required, other basic options after clicking on 'add new'
                // required,
                // nest the CompanyToDo stuff inside under 'new_company_todo' key -- that's what RfxCompanyToDoSerializer expects
                new_company_todo: {
                    category,
                },
            };

            data.new_company_todo[category] = { title: title };  // the data for the actual linked model (quotesheet, docrequest, etc.)
            // start with just 'title' then add more (or overwrite) for certain categories - I don't like using destructuring in places like this...

            if (category == 'quotesheet') {
                const quoteSheetData = {
                    title: title,
                    quotesheet_template_id: quotesheet_template_id,  // if its null or '', that's OK for backend (optional) - not all tenants have these
                };
                data.new_company_todo['quotesheet'] = quoteSheetData;
            }

            // overwrite it for specific types
            if (category == "docrequest") {
                const docRequestData = {
                    title: title,
                    dr_type: dr_type,
                };
                data.new_company_todo['docrequest'] = docRequestData;
            }

            if (category == "smartform") {
                const smartformOptions = {
                    name: title,
                    template_form: smartform_template_id,
                };
                data.new_company_todo['smartform'] = smartformOptions;
            }

            console.log("createRfxCompanyToDo: request: ", data);

            return suppliosApi.rfx
                .createRfxCompanyToDo(rfxId, data)
                .then((response) => {
                    const result = response.data;

                    console.log("createRfxCompanyToDo: result: ", result);

                    // this instantiates new class instance, pushes to list, and starts edit
                    this.handleNewToDo(result);

                    return true;
                })
                .catch((error) => {
                    this.apiErrorHandler(error);

                    return false;
                })
                .then((success) => {
                    this.creating = false;

                    return success;
                });
        },
        handleNewToDo: function (newToDoData) {
            // instantiate new class instance and push to local list
            let newToDoInst = new RfxCompanyToDo(newToDoData);
            // ADD TO BOTTOM OF LIST - this.rfxCompanyToDos.push(newToDoInst);
            this.rfxCompanyToDos.unshift(newToDoInst);
            this.editToDo(newToDoInst);
        },
        editToDo: function (toDoObj) {
            // toDoObj should be INSTANCE of RfxCompanyToDo class
            // IMPORTANT - call all edits from here -- so you can track exactly what RfxCompanyToDo is being edited
            console.log("editToDo: ", toDoObj);
            // check if any modals are already open?  if so, block any other action?
            this.activeRfxCompanyToDoId = toDoObj.id; // RESET THIS WHEN DONE EDITING
            // check category --> set the active linked_model ID --> open correct modal
            const { category, linked_model_id } = toDoObj;

            switch (category) {
                case "docrequest":
                    // check type also?  template or upload?
                    this.activeDocRequestId = linked_model_id;
                    this.docRequestModalShowing = true;
                    break;
                case "formium_form":
                    this.activeFormiumFormId = linked_model_id;
                    this.formiumFormModalShowing = true;
                    break;
                case "quotesheet":
                    this.activeQuoteSheetId = linked_model_id;
                    this.quoteSheetModalShowing = true;
                    break;
                case "smartform":
                    this.activeSmartFormId = linked_model_id;
                    // OLD VERSION this.isSmartFormModalShown = true;
                    this.smartFormModalShowing = true;
                    break;
            }
        },
        finishEditToDo: function () {
            // Call this anytime a modal closes / hides -- refresh that ToDo from API (or refresh the entire list?), reset the active RfxCompanyToDoId
            if (this.activeRfxCompanyToDoId) {
                // you SHOULD have this.activeRfxCompanyToDoId if this function gets called, but wrapping in if stmt just in case
                this.refreshToDoFromApi(this.activeRfxCompanyToDoId);
            }
            this.activeRfxCompanyToDoId = ""; // reset this immediately after calling refresh... don't wait for API response... in case user opens another modal
        },
        refreshToDoFromApi: function (rfxCompanyToDoId) {
            // call after editing / closing a to do modal -- get refreshed version from API, use splice to replace it in the array
            this.refreshToDoId = rfxCompanyToDoId; // this will change appearance of todo on the list
            suppliosApi.rfxCompanyToDo
                .retrieve(rfxCompanyToDoId)
                .then((response) => {
                    let refreshedToDo = new RfxCompanyToDo(response.data);
                    this.replaceToDoInList(refreshedToDo);
                    console.log(
                        `just refreshed rfxCompanyToDo ID ${rfxCompanyToDoId}`
                    );
                })
                .catch((error) => this.apiErrorHandler(error))
                .then(() => (this.refreshToDoId = "")); // reset the refresh ID
        },
        replaceToDoInList: function (rfxCompanyToDoInst, onlyDelete) {
            // pass in an INSTANCE of RfxCompanyToDo class
            // if onlyDelete === true, then DO NOT put instance back in list, just delete the one you find -- we use this for deletes and for refreshes
            let foundIndex = this.rfxCompanyToDos.findIndex(
                (todo) => todo.id === rfxCompanyToDoInst.id
            ); // returns -1 if not found
            if (foundIndex === -1) return;
            if (onlyDelete) {
                this.rfxCompanyToDos.splice(foundIndex, 1); // no adding new instance, only delete
            } else {
                this.rfxCompanyToDos.splice(foundIndex, 1, rfxCompanyToDoInst); // add the new instance
            }
            console.log(
                `just replaced RfxCompanyToDo with new one, or deleted it from list:`
            );
            console.log(rfxCompanyToDoInst);
        },
        async startClone(rctdObj) {
            // create clone, then get response, then re-order list?
            console.log('starting clone for RCTD below:', rctdObj);
            this.loadingClone = true;
            // need to get the index of the object we are cloning -- but we can't use 'position' -- it doesn't update...
            //const insertIndex = rctdObj.position; 
            let insertIndex = 0;  // default to 0
            let origIndex = this.rfxCompanyToDos.findIndex(todo => todo.id === rctdObj.id);
            if (origIndex === -1) {
                insertIndex = this.rfxCompanyToDos.length;  // if not found, just put it at the end
            } else {
                insertIndex = origIndex + 1;  // put it after the one we're cloning
            }
            const rctdId = rctdObj.id;
            let resp;
            let result;
            const overrideData = {};  // allows nested dicts for company_todo and all linked models 
            const oldTitle = rctdObj.companyToDo.title;
            // make below stuff a method on RfxCompanyToDo class?  .get_clone_data() or something?
            const newTitle = `Copy of ${oldTitle}`;  // might be name or title depending on todo category 
            if (rctdObj.category == 'smartform') {
                overrideData['company_todo'] = {
                    smartform: {name: newTitle},  // smartform uses name, not title 
                };
            } else if (rctdObj.category == 'docrequest') {
                overrideData['company_todo'] = {
                    docrequest: {title: newTitle},  // docrequest uses title, not name 
                };
            } else if (rctdObj.category == 'quotesheet') {
                overrideData['company_todo'] = {
                    quotesheet: {title: newTitle},  // quotesheet uses title, not name 
                };
            } else if (rctdObj.category == 'formium_form') {
                overrideData['company_todo'] = {
                    formium_form: {title: newTitle},  // formium_form uses title, not name 
                };
            }
            try {
                resp = await suppliosApi.rfxCompanyToDo.clone(rctdId, overrideData);
            } catch (err) {
                this.apiErrorHandler(err);
                return false;
            }
            if (resp?.data) {
                // successful?  create new instance here, insert into list, adjust list positions?, show success message, open for editing
                // splicing it into the list should trigger the list watcher to update the list metadata via api...
                const newRctd = new RfxCompanyToDo(resp.data);
                this.rfxCompanyToDos.splice(insertIndex, 0, newRctd); // insert at index 1, delete 0
                this.$toast.success(`Successfully cloned ${rctdObj.title}`);
                // adjust list positions?  that SHOULD happen automatically when you update the list
                // ... but you might want reconsider that.... seems wonky
                // finally, open for editing
                this.editToDo(newRctd);
                result = true;
            } else {
                this.$toast.error(`Error cloning ${rctdObj.title}`);
                result = false;
            }
            this.loadingClone = false;
            return result;
        },
        startDelete: function (rctdObj) {
            // set this.deleteObject, show modal
            this.deleteRfxCompanyToDoId = rctdObj.id; // set visual indicator that we're going to delete
            this.deleteObject = cloneDeep(rctdObj); // or do you need Object.assign() or cloneDeep?  rctdObj is INSTANCE of RfxCompanyToDo
            this.deleteModalShowing = true;
        },
        performDelete: function () {
            if (this.allDisabled) {
                return;  // just bail if things are read only 
            }
            // actually do the delete, then show confirm/error message? then close modal
            let deleteId = this.deleteObject.id;
            let deleteTitle = this.deleteObject.title;
            let postData = { confirm_message: this.deleteConfirmMessage }; // must add in { confirm_message: 'delete' } if there are already replies!
            return suppliosApi.rfxCompanyToDo
                .deleteTodo(deleteId, postData)
                .then((response) => {
                    console.log("Delete successful - response below:");
                    console.log(response.data);
                    this.$toast.success(`Successfully deleted ${deleteTitle}`);
                    // DON'T RE-FETCH THE LIST -- not needed, jarring UI - this.getRfxCompanyToDos();  // triggers it, but doesn't wait for it
                    // delete from list using splice - since we're not re-fetching list, wait to do this until we know delete was successful
                    let onlyDelete = true;
                    this.replaceToDoInList(this.deleteObject, onlyDelete);
                    this.deleteModalShowing = false;
                    return true;
                })
                .catch((err) => {
                    if (err.response.status === 409) {
                        // 409 on this endpoint means we have replies and did not have confirm_message: 'delete'
                        // just keep modal showing in this case?  highlight that user must enter text?
                        console.log("Error 409!");
                        console.log(err.response.data);
                        this.highlightDeleteConfirmMessage = true;
                    } else {
                        this.apiErrorHandler(err);
                        // we'll hide the modal so it isn't stuck open
                        this.deleteModalShowing = false;
                    }
                    return false;
                });
            // no then/finally... do it all in each try/catch above
        },
        onDeleteModalHidden: function () {
            // reset things related to delete - deleteObject, deleteConfirmMessage
            this.deleteObject = Object.assign({});
            this.deleteConfirmMessage = "";
            this.deleteRfxCompanyToDoId = ""; // this was for showing visual indicator of which one is being deleted
            this.highlightDeleteConfirmMessage = false;
        },
        updateToDoList: function () {
            if (this.allDisabled) {
                return;  // just bail if things are read only 
            }
            // call this to update all the 'metadata' for the RfxCompanyToDo list items (position, required) - NOT for nested linked_models
            this.updating = true;
            let listData = this.rfxCompanyToDos.map((todo, index) =>
                todo.formatForApi(index)
            ); // outputs a new array
            console.log("updating RfxCompanyToDo list metadata:");
            console.log(listData);
            suppliosApi.rfx
                .updateRfxCompanyToDoList(this.rfxId, listData)
                .then((response) => {
                    // do anything with response?
                    console.log("Updated list metadata - response.data below");
                    console.log(response.data);
                })
                .catch((error) => this.apiErrorHandler(error))
                .then(() => (this.updating = false));
        },
        dumpApiData: function () {
            let apiData = [];
            this.rfxCompanyToDos.forEach((todo, index) => {
                apiData.push(todo.formatForApi(index));
            });
            console.log(apiData);
        },
        // OLDER...
        saveAll: function () {
            // called by parent if setup wizard
            // must return PROMISE that resolves to Boolean if all is OK
            // should also call any validation and show errors if any
            let saveSuccessPromise;
            if (this.totalItemCount < 1) {
                this.handleTempError(
                    "You must add at least one Response item of any type."
                );
                saveSuccessPromise = Promise.resolve(false);
            }
            this.showTempError = false;
            this.error = false;
            this.showSaveSuccess();
            saveSuccessPromise = Promise.resolve(true);
            return {
                statusPromise: saveSuccessPromise,
                successMessage: "Response format saved",
            };
        },
        okToContinue: function (tabChangeEvent) {
            // PUBLIC METHOD CALLED FROM PARENT - takes tabChangeEvent as arg, return Promise that resolves to boolean where true = can continue
            console.log(
                "from component okToContinue, tabChangeEvent is below:"
            );
            console.log(tabChangeEvent);
            return Promise.resolve(true); // change later
        },
        showSaveSuccess: function (msg) {
            // separating out save success message toast for DRY
            let successMsg = msg ? msg : "Response format saved";
            this.$toast.success(successMsg);
        },
        // PUBLIC METHODS ABOVE, private methods below
        handleError: function (error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        handleTempError: function (errorText) {
            this.tempErrorText = errorText;
            this.showTempError = true;
        },
        showPriceSheet: function (priceSheetPk) {
            this.activePriceSheetPk = priceSheetPk;
            this.showPriceSheetModal = true;
        },
        showFormBuilder: function (rfxFormPk) {
            console.log("from showFormBuilder our rfxFormPk is");
            console.log(rfxFormPk);
            this.activeFormBuilderPk = rfxFormPk.toString();
            this.showFormBuilderModal = true;
        },
    },
};
</script>

<style lang="scss">
.add-todo-box {
    $add-todo-transition-duration: 0.2s;
    flex: 1 1 0px; // flex-grow flex-shrink flex-basis
    //background-color: var(--gray-100);
    background-color: #fff;
    transition: $add-todo-transition-duration;
    cursor: pointer;
    box-shadow: $box-shadow-sm;
    overflow: hidden;

    .action-area {
        background-color: var(--success-50);
        color: var(--success-600);
        transition: $add-todo-transition-duration;
        .add-text {
            color: var(--success-200);
            transition: $add-todo-transition-duration;
        }
    }

    &:hover {
        background-color: #fff;
        box-shadow: $box-shadow;

        .action-area {
            background-color: var(--success-100);
            color: var(--success-800);
            .add-text {
                color: var(--success-800);
            }
        }
    }
    /* margin: 0 1rem;
    &:first-child {
        margin-left: 0;
    }
    &:last-child {
        margin-right: 0;
    } */
}

.rctd-todo {
    background-color: var(--gray-50);
    &.hidden-todo {
        background-color: var(--gray-150);
    }
}

// TRANSITION GROUP
// .rfxcompanytodo-list is name on transition-group - .rfxcompanytodo-list-item class is on individual draggable items in list
/* .rfxcompanytodo-list-item {
    transition: all 1s;
} */
.rfxcompanytodo-list-enter-active,
.rfxcompanytodo-list-leave-active {
    transition: all 0.6s;
}
/* .rfxcompanytodo-list-leave-active {
    position: absolute;
} */
.rfxcompanytodo-list-enter,
.rfxcompanytodo-list-leave-to {
    opacity: 0;
}
.rfxcompanytodo-list-move {
    transition: transform 0.6s;
}
</style>
